/**
 * @fileoverview Request-response tracking for async operations over postMessage.
 * Enables Promise-based APIs for operations that need host responses (e.g., confirm dialogs).
 */

import { postToHost } from "./messenger";
import { logger } from "./logger";

// ============================================================================
// Types
// ============================================================================

/**
 * Pending request entry with resolve/reject functions and timeout.
 */
interface PendingRequest<T = unknown> {
  resolve: (value: T) => void;
  reject: (reason: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
  event: string;
}

// ============================================================================
// State
// ============================================================================

/**
 * Map of pending requests by requestId.
 */
const pendingRequests = new Map<string, PendingRequest>();

/**
 * Default timeout for requests (30 seconds).
 */
const DEFAULT_REQUEST_TIMEOUT = 30000;

// ============================================================================
// Request ID Generation
// ============================================================================

/**
 * Generate a unique request ID.
 * Format: req_{timestamp}_{random}
 */
export function generateRequestId(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 9);
  return `req_${timestamp}_${random}`;
}

// ============================================================================
// Request/Response Functions
// ============================================================================

/**
 * Send a request to the host and return a Promise that resolves when the response is received.
 *
 * @param event - The event name to send
 * @param data - Additional data to send with the request
 * @param timeout - Timeout in milliseconds (defaults to 30000)
 * @returns Promise that resolves with the response data
 *
 * @example
 * ```typescript
 * const result = await sendRequest<{ confirmed: boolean }>(
 *   'embedded::ui.confirm',
 *   { title: 'Delete?', message: 'Are you sure?' }
 * );
 * if (result.confirmed) {
 *   // User confirmed
 * }
 * ```
 */
export function sendRequest<T>(
  event: string,
  data: Record<string, unknown> = {},
  timeout: number = DEFAULT_REQUEST_TIMEOUT,
): Promise<T> {
  const requestId = generateRequestId();

  return new Promise<T>((resolve, reject) => {
    // Set up timeout
    const timer = setTimeout(() => {
      const pending = pendingRequests.get(requestId);
      if (pending) {
        pendingRequests.delete(requestId);
        reject(
          new Error(
            `[EmbeddedSDK] Request "${event}" timed out after ${timeout}ms`,
          ),
        );
      }
    }, timeout);

    // Store the pending request
    pendingRequests.set(requestId, {
      resolve: resolve as (value: unknown) => void,
      reject,
      timeout: timer,
      event,
    });

    // Send the request to host with requestId as base field
    postToHost(event, data, "*", requestId);
  });
}

/**
 * Handle a response from the host for a pending request.
 * Called by the message listener when a response event is received.
 *
 * @param requestId - The request ID from the response
 * @param result - The result data to resolve with
 * @param error - Optional error message to reject with
 *
 * @example
 * ```typescript
 * // In message listener:
 * onMessage('embedded::ui.confirm.response', (msg) => {
 *   handleResponse(msg.requestId, { confirmed: msg.confirmed });
 * });
 * ```
 */
export function handleResponse<T = unknown>(
  requestId: string,
  result: T,
  error?: string,
): void {
  const pending = pendingRequests.get(requestId);

  if (!pending) {
    // Request may have already timed out or been handled
    logger.warn(`Received response for unknown request: ${requestId}`);
    return;
  }

  // Clear the timeout
  clearTimeout(pending.timeout);

  // Remove from pending requests
  pendingRequests.delete(requestId);

  // Resolve or reject the promise
  if (error) {
    pending.reject(new Error(error));
  } else {
    pending.resolve(result);
  }
}

/**
 * Check if there are any pending requests.
 * Useful for cleanup or debugging.
 */
export function hasPendingRequests(): boolean {
  return pendingRequests.size > 0;
}

/**
 * Get the count of pending requests.
 * Useful for debugging.
 */
export function getPendingRequestCount(): number {
  return pendingRequests.size;
}

/**
 * Cancel all pending requests.
 * Useful for cleanup when the SDK is being destroyed.
 *
 * @param reason - Optional reason for cancellation
 */
export function cancelAllRequests(reason: string = "SDK cleanup"): void {
  pendingRequests.forEach((pending, requestId) => {
    clearTimeout(pending.timeout);
    pending.reject(
      new Error(`[EmbeddedSDK] Request ${requestId} cancelled: ${reason}`),
    );
  });
  pendingRequests.clear();
}

/**
 * Cancel a specific pending request.
 *
 * @param requestId - The request ID to cancel
 * @param reason - Optional reason for cancellation
 * @returns true if the request was found and cancelled, false otherwise
 */
export function cancelRequest(
  requestId: string,
  reason: string = "Cancelled",
): boolean {
  const pending = pendingRequests.get(requestId);

  if (!pending) {
    return false;
  }

  clearTimeout(pending.timeout);
  pendingRequests.delete(requestId);
  pending.reject(
    new Error(`[EmbeddedSDK] Request ${requestId} cancelled: ${reason}`),
  );

  return true;
}
